import {
  BarChart3,
  Building2,
  CalendarDays,
  Clock,
  Edit3,
  Globe,
  Megaphone,
  MessageSquare,
  Newspaper,
  RefreshCw,
  Settings,
  Sparkles
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Loader from "../../../components/loaders/loader";
import { Header } from "../../../components/page-header/Header";
import { useCheckUrlQuery, useCreateTemplateMutation, useGetTemplateQuery } from "../../../store/Reducer/templateApis";
import { EXAMPLE_PROMPTS, isAiSite } from "../../ai-site-builder/siteModel";

const hasSettings = (map) => map && typeof map === "object" && Object.keys(map).length > 0;

// The masjid's website at a glance: a live thumbnail, its status, and the
// way into the AI builder.
const WebsiteCard = ({ doc, domain }) => {
  const liveIsAi = isAiSite(doc?.templateMap);
  const draftIsAi = isAiSite(doc?.draftMap);
  const hasLive = hasSettings(doc?.templateMap);
  const liveUrl = hasLive && doc?.templateUrlPath ? `${domain}/${doc.templateUrlPath}` : null;
  const hasAiSite = liveIsAi || draftIsAi;
  const [status, tone] = draftIsAi
    ? ["Draft not published", "bg-amber-100 text-amber-800"]
    : liveIsAi
      ? ["Live", "bg-green-100 text-green-800"]
      : hasLive
        ? ["Live (old template)", "bg-gray-100 text-gray-700"]
        : ["Not built yet", "bg-gray-100 text-gray-700"];

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-gray-200 p-5 md:flex-row">
      <div className="relative h-[200px] w-full shrink-0 overflow-hidden rounded-lg border bg-gray-50 md:w-[320px]">
        {liveUrl ? (
          <iframe title="Your website" src={liveUrl} loading="lazy" tabIndex={-1} aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 h-[800px] w-[1280px] origin-top-left scale-[0.25] border-0" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
            <Globe size={28} />
            <span className="text-sm">No website yet</span>
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Your website</h3>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>{status}</span>
        </div>
        {doc?.templateUrlPath && <p className="mt-1 truncate text-sm text-gray-500">{domain}/{doc.templateUrlPath}</p>}
        <p className="mt-3 text-sm text-gray-600">
          {hasAiSite
            ? "Change any text or photo by clicking it, or ask the AI to redesign a section. Events, news and prayer times update on their own."
            : "Describe the website you want and the AI designs it for you, with your events, news, prayer times and contact form built in."}
        </p>
        {hasLive && !liveIsAi && (
          <p className="mt-2 rounded-lg bg-blue-50 p-2.5 text-sm text-blue-800">Your current website stays live until you publish your new AI website.</p>
        )}
        <div className="mt-auto flex flex-wrap gap-2 pt-4">
          {hasAiSite ? (
            <>
              <Link to="/user-dashboard/ai-site" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                <Edit3 className="h-4 w-4" /> Edit
              </Link>
              <Link to="/user-dashboard/ai-site?new=1" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <RefreshCw className="h-4 w-4" /> Start over with a new prompt
              </Link>
            </>
          ) : (
            <Link to="/user-dashboard/ai-site?new=1" className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700">
              <Sparkles className="h-4 w-4" /> Build with AI
            </Link>
          )}
          {liveUrl && (
            <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Globe className="h-4 w-4" /> Visit
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Once the site is built, what keeps it current is the masjid's own data: the
// site shows these by itself, so they are one click away here.
const LIVE_CONTENT = [
  { to: "/user-dashboard/prayer-times/main", icon: Clock, title: "Prayer times", text: "Athan, Iqamah and Jumu'ah times" },
  { to: "/user-dashboard/user-events", icon: CalendarDays, title: "Events", text: "Upcoming classes, talks and gatherings" },
  { to: "/user-dashboard/user-news", icon: Newspaper, title: "News", text: "Updates for your community" },
  { to: "/user-dashboard/user-announcements", icon: Megaphone, title: "Announcements", text: "Banners and notices on every page" },
  { to: "/user-dashboard/masjid-information", icon: Building2, title: "Masjid information", text: "Address, phone, logo and facilities" },
  { to: "/user-dashboard/messages", icon: MessageSquare, title: "Messages", text: "What visitors send from your contact form" },
];

const LiveContentLinks = () => (
  <div className="mt-8">
    <h3 className="text-base font-semibold text-gray-900">Keep your website up to date</h3>
    <p className="mb-3 text-sm text-gray-500">Your website shows these by itself. Change them here and the site updates straight away.</p>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {LIVE_CONTENT.map(({ to, icon: Icon, title, text }) => (
        <Link key={to} to={to}
          className="group flex items-start gap-3 rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:bg-blue-50/40">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100">
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-gray-900">{title}</span>
            <span className="block text-sm text-gray-500">{text}</span>
          </span>
        </Link>
      ))}
    </div>
  </div>
);

// Only before the first build: example descriptions to start from. (The
// builder shows them too, also when starting over.)
const StartIdeas = () => (
  <div className="mt-8">
    <h3 className="text-base font-semibold text-gray-900">Not sure what to ask for? Start from an idea</h3>
    <p className="mb-3 text-sm text-gray-500">Pick one and adjust it to your masjid.</p>
    <div className="grid gap-3 sm:grid-cols-2">
      {EXAMPLE_PROMPTS.map((prompt) => (
        <Link key={prompt} to={`/user-dashboard/ai-site?new=1&prompt=${encodeURIComponent(prompt)}`}
          className="flex items-start gap-3 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 hover:border-violet-300 hover:bg-violet-50/50">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" /> {prompt}
        </Link>
      ))}
    </div>
  </div>
);

// Website address: masjidhub.org/sites/<path>. Checks the new address as you
// type and only saves one that is free.
const AddressSettings = ({ doc, domain }) => {
  const current = doc?.templateUrlPath || "";
  const [path, setPath] = useState(current);
  const [debounced, setDebounced] = useState(current);
  const [createTemplate, { isLoading: saving }] = useCreateTemplateMutation();
  useEffect(() => setPath(current), [current]);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(path.trim()), 400);
    return () => clearTimeout(id);
  }, [path]);
  const changed = debounced && debounced !== current;
  const { data: check, isFetching } = useCheckUrlQuery({ urlpath: debounced }, { skip: !changed });
  const canSave = changed && path.trim() === debounced && check?.available && !isFetching;
  const liveUrl = current ? `${domain}/${current}` : "";

  const save = async () => {
    if (!window.confirm(`Change your website address to ${domain}/${debounced}?\n\nThe old address will stop working, so update any links or QR codes that use it.`)) return;
    try {
      const res = await createTemplate({ templateUrlPath: debounced }).unwrap();
      toast.success(res?.message || "Website address updated");
    } catch (err) {
      toast.error(err?.data?.message || "Couldn't change the address.");
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-800">Website settings</h2>

      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800">Website address</h3>
        {!doc ? (
          <p className="text-sm text-gray-500">Build your website first; it gets an address when it's created.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-4 py-3">
              <span className="text-sm text-gray-600 break-all">{liveUrl}</span>
              <a className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                href={liveUrl} target="_blank" rel="noreferrer">
                <Globe className="w-4 h-4" /> Visit
              </a>
            </div>
            <label htmlFor="site-path" className="block text-sm font-medium text-gray-700">Change the address</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex flex-grow items-center rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 overflow-hidden">
                <span className="hidden sm:block bg-gray-100 px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{domain}/</span>
                <input
                  id="site-path"
                  type="text"
                  value={path}
                  maxLength={50}
                  onChange={(e) => setPath(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                  className="w-full px-3 py-2 text-sm outline-none"
                />
              </div>
              <button type="button" onClick={save} disabled={!canSave || saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed">
                {saving ? "Saving…" : "Save address"}
              </button>
            </div>
            {changed && (
              <p role="status" className={`text-sm ${isFetching ? "text-gray-500" : check?.available ? "text-green-700" : "text-red-600"}`}>
                {isFetching ? "Checking…" : check?.message}
              </p>
            )}
          </>
        )}
      </div>

      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5">
        <h3 className="text-lg font-semibold text-gray-800">Your own domain</h3>
        <p className="text-sm text-gray-500">Using your own domain (like www.yourmasjid.org) is coming soon.</p>
      </div>

      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-gray-400" />
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Website analytics</h3>
            <p className="text-sm text-gray-500">Coming soon</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { id: "editor", icon: Edit3, label: (t) => t("WebsiteButton") },
  { id: "settings", icon: Settings, label: (t) => t("SettingsButton") },
];

const MasjidWeb = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("editor");
  const { data, isLoading: templateLoading } = useGetTemplateQuery(undefined, { refetchOnMountOrArgChange: true });
  const savedTemplate = data?.data;
  const domain = data?.domain || `${process.env.REACT_APP_API_URL || "https://masjidhub.org"}/sites`;

  const breadcrumbItems = [
    { label: t("universal_dashboard_text"), to: "/user-dashboard" },
    { label: t("masjidWeb_breadcurmbs_title") },
  ];

  return (
    <section className="min-h-[86vh] bg-gradient-to-b from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-center justify-between">
          <Header title={t("masjidWebTitle")} breadcrumbItems={breadcrumbItems} />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8" role="tablist">
          {TABS.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              onClick={() => setActiveTab(id)}
              className={`p-5 rounded-xl shadow-sm transition-all duration-200 border flex items-center gap-3 ${activeTab === id ? "bg-blue-500 text-white shadow-blue-200" : "bg-white hover:bg-gray-50"}`}
            >
              <Icon className={`w-5 h-5 ${activeTab === id ? "text-white" : "text-blue-500"}`} />
              <span className="font-semibold">{label(t)}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm px-4 sm:px-8 py-6">
          {activeTab === "editor" && (
            <>
              <div className="pb-4 space-y-2">
                <h2 className="text-2xl font-bold text-gray-800">{t("WebsiteEditorHeading")}</h2>
                <p className="text-gray-600">Build your website from a description, then change anything on it.</p>
              </div>

              {templateLoading ? <Loader /> : <WebsiteCard doc={savedTemplate} domain={domain} />}

              {!templateLoading && (isAiSite(savedTemplate?.templateMap) || isAiSite(savedTemplate?.draftMap)
                ? <LiveContentLinks />
                : <StartIdeas />)}
            </>
          )}

          {activeTab === "settings" && (templateLoading ? <Loader /> : <AddressSettings doc={savedTemplate} domain={domain} />)}
        </div>
      </div>
    </section>
  );
};

export default MasjidWeb;
